import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../poker-ui/utils/cn';
import { useAudio } from '../poker-ui/AudioProvider';

function clampNonNegInt(n) {
  return Math.max(0, Math.floor(Number(n) || 0));
}

function toSignedInt(n) {
  return Math.trunc(Number(n) || 0);
}

/** Счёт SEKA/BUCKS/респект: от from к to при смене outcomeToken (ease-out cubic). */
function OverlayCountNumber({
  from,
  to,
  outcomeToken,
  durationMs = 840,
  className,
  locale = 'ru-RU',
  signed = false,
  enableBalanceGainSfx = false,
  /** Тот же loop счёта, что у денег; на конце — nishtjak, не money-na-balanse. */
  enableRespectGainSfx = false,
  /** Звук после накрутки респекта (по умолчанию nishtjak; при крупном SEKA — ha-ha). */
  respectCompleteSoundKey = 'overlay-respect-complete',
}) {
  const { play, playLoop, stopLoop, initAudio } = useAudio();
  const fromN = signed ? toSignedInt(from) : clampNonNegInt(from);
  const toN = signed ? toSignedInt(to) : clampNonNegInt(to);
  const [n, setN] = useState(fromN);
  const rafRef = useRef(null);
  const moneyLoopIdRef = useRef(null);

  useLayoutEffect(() => {
    setN(fromN);
  }, [outcomeToken, fromN]);

  useEffect(() => {
    if (moneyLoopIdRef.current) {
      stopLoop(moneyLoopIdRef.current);
      moneyLoopIdRef.current = null;
    }
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (fromN === toN) {
      setN(toN);
      return undefined;
    }
    const countingUp = toN > fromN;
    const useCountingLoop = countingUp && (enableBalanceGainSfx || enableRespectGainSfx);
    if (useCountingLoop) {
      initAudio();
      const id = playLoop('overlay-money-counting', 0.92);
      if (id) moneyLoopIdRef.current = id;
    }
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / durationMs);
      const eased = 1 - (1 - p) ** 3;
      setN(Math.round(fromN + (toN - fromN) * eased));
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        if (moneyLoopIdRef.current) {
          stopLoop(moneyLoopIdRef.current);
          moneyLoopIdRef.current = null;
        }
        if (enableBalanceGainSfx && countingUp) {
          play('overlay-money-balance', 1);
        } else if (enableRespectGainSfx && countingUp) {
          play(respectCompleteSoundKey, 1);
        }
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (moneyLoopIdRef.current) {
        stopLoop(moneyLoopIdRef.current);
        moneyLoopIdRef.current = null;
      }
    };
  }, [
    fromN,
    toN,
    outcomeToken,
    durationMs,
    enableBalanceGainSfx,
    enableRespectGainSfx,
    respectCompleteSoundKey,
    initAudio,
    play,
    playLoop,
    stopLoop,
  ]);

  const display = signed ? toSignedInt(n) : clampNonNegInt(n);
  const sign = signed && display > 0 ? '+' : '';
  return <span className={className}>{sign}{display.toLocaleString(locale)}</span>;
}

/** Компактная полоса респекта внутри уровня; level-up / level-down / тот же уровень. */
function SessionRespectProgress({ pb, pa, outcomeToken, barGradientClass }) {
  const pbLv = Number(pb?.level) || 1;
  const paLv = Number(pa?.level) || 1;
  const sameLevel = pbLv === paLv;
  const levelUp = paLv > pbLv;
  const fromMax = Math.max(1, Number(pb?.xpMax) || Number(pa?.xpMax) || 1);
  const toMax = Math.max(1, Number(pa?.xpMax) || Number(pb?.xpMax) || 1);
  const { fromPct, toPct } = useMemo(() => {
    if (sameLevel) {
      return {
        fromPct: Math.min(100, (clampNonNegInt(pb?.xpCurrent) / fromMax) * 100),
        toPct: Math.min(100, (clampNonNegInt(pa?.xpCurrent) / toMax) * 100),
      };
    }
    if (levelUp) {
      return {
        fromPct: 0,
        toPct: Math.min(100, (clampNonNegInt(pa?.xpCurrent) / toMax) * 100),
      };
    }
    return {
      fromPct: Math.min(100, (clampNonNegInt(pb?.xpCurrent) / fromMax) * 100),
      toPct: Math.min(100, (clampNonNegInt(pa?.xpCurrent) / toMax) * 100),
    };
  }, [pb?.xpCurrent, pa?.xpCurrent, fromMax, toMax, sameLevel, levelUp]);

  const [widthPct, setWidthPct] = useState(fromPct);
  const rafRef = useRef(null);

  useLayoutEffect(() => {
    setWidthPct(fromPct);
  }, [outcomeToken, fromPct]);

  useEffect(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const t0 = performance.now();
    const dur = 900;
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      const eased = 1 - (1 - p) ** 3;
      setWidthPct(fromPct + (toPct - fromPct) * eased);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [fromPct, toPct, outcomeToken]);

  return (
    <div className="mt-2 rounded-lg border border-white/10 bg-black/40 px-2 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-white/45">Респект</p>
        <div className="flex items-center gap-1.5">
          {!sameLevel ? (
            <span className="rounded bg-white/12 px-1 py-0.5 text-[8px] font-black uppercase tracking-wide text-amber-200/95">
              {levelUp ? 'Lv↑' : 'Lv↓'}
            </span>
          ) : null}
          <span className="text-[9px] font-semibold tabular-nums text-white/70">
            Ур. {pa.level}
          </span>
        </div>
      </div>
      <div
        className="relative mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-black/55 ring-1 ring-black/40"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(widthPct)}
      >
        <div
          className={cn(
            'h-full rounded-full bg-gradient-to-r shadow-[0_0_14px_rgba(255,255,255,0.18)]',
            barGradientClass
          )}
          style={{ width: `${widthPct}%`, minWidth: widthPct > 0.5 ? '3px' : 0 }}
        />
      </div>
      <div className="mt-1 flex items-center justify-between text-[8px] tabular-nums text-white/38">
        <span>
          {clampNonNegInt(pb.xpCurrent)}
          {pb.xpMax > 0 ? `/${pb.xpMax}` : ''}
        </span>
        <span className="text-white/50">
          {clampNonNegInt(pa.xpCurrent)}
          {pa.xpMax > 0 ? `/${pa.xpMax}` : ''}
        </span>
      </div>
      {String(pb.rankName || '') !== String(pa.rankName || '') ? (
        <p className="mt-0.5 truncate text-center text-[8px] text-white/40">
          {String(pb.rankName || '')} → <span className="text-white/60">{String(pa.rankName || '')}</span>
        </p>
      ) : null}
    </div>
  );
}

// Компонент для анимированных уведомлений
export const NotificationToast = ({ message, type = 'success', onClose }) => {
  const toastRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (toastRef.current) {
        toastRef.current.style.transform = 'translateX(100%)';
        toastRef.current.style.opacity = '0';
        setTimeout(() => onCloseRef.current?.(), 300);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const getToastStyle = () => {
    const baseStyle = {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '15px 20px',
      borderRadius: '10px',
      color: 'white',
      fontWeight: '600',
      zIndex: 10000,
      transform: 'translateX(0)',
      opacity: '1',
      transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      minWidth: '250px',
      textAlign: 'center'
    };

    switch (type) {
      case 'success':
        return {
          ...baseStyle,
          background: 'linear-gradient(145deg, rgba(40, 167, 69, 0.9), rgba(32, 201, 151, 0.9))',
          borderColor: 'rgba(40, 167, 69, 0.3)'
        };
      case 'error':
        return {
          ...baseStyle,
          background: 'linear-gradient(145deg, rgba(220, 53, 69, 0.9), rgba(200, 35, 51, 0.9))',
          borderColor: 'rgba(220, 53, 69, 0.3)'
        };
      case 'warning':
        return {
          ...baseStyle,
          background: 'linear-gradient(145deg, rgba(255, 193, 7, 0.9), rgba(253, 126, 20, 0.9))',
          borderColor: 'rgba(255, 193, 7, 0.3)',
          color: '#333'
        };
      case 'info':
        return {
          ...baseStyle,
          background: 'linear-gradient(145deg, rgba(0, 123, 255, 0.9), rgba(0, 86, 179, 0.9))',
          borderColor: 'rgba(0, 123, 255, 0.3)'
        };
      default:
        return baseStyle;
    }
  };

  return (
    <div ref={toastRef} style={getToastStyle()}>
      {message}
    </div>
  );
};

export const SessionResultOverlay = ({ outcome, onClose, autoDismiss = true }) => {
  const onCloseRef = useRef(onClose);
  const outcomeToken = String(outcome?.outcomeId || '');
  /** Итог матч-аборта (крыса / компенсация): ждём полный цикл тикера + пауза, до dismiss не «отпускаем». */
  const isMatchAbortOutcome = outcomeToken.startsWith('match_abort:');
  const result = String(outcome?.result || 'draw');
  const style = String(outcome?.display?.style || (result === 'win' ? 'victory' : result === 'lose' ? 'defeat' : 'neutral'));
  const isWin = style === 'victory';
  const isLose = style === 'defeat';
  const respectDeltaRaw =
    outcome?.respectDelta != null && Number.isFinite(Number(outcome.respectDelta))
      ? Math.trunc(Number(outcome.respectDelta))
      : Math.trunc(Number(outcome?.respectGain) || 0);
  const sekaAfter = Math.max(0, Number(outcome?.balanceAfter?.seka) || 0);
  const bucksAfter = Math.max(0, Number(outcome?.balanceAfter?.bucks) || 0);
  const unlocked = Array.isArray(outcome?.road?.unlockedMilestones) ? outcome.road.unlockedMilestones : [];
  const pb = outcome?.progressBefore && typeof outcome.progressBefore === 'object' ? outcome.progressBefore : null;
  const pa = outcome?.progress && typeof outcome.progress === 'object' ? outcome.progress : null;
  const tableChips =
    outcome?.heroTableChips != null && Number.isFinite(Number(outcome.heroTableChips))
      ? Math.max(0, Math.floor(Number(outcome.heroTableChips)))
      : null;
  const sekaDelta = outcome?.currencyDelta?.seka;
  const sekaDeltaNum = sekaDelta != null && Number.isFinite(Number(sekaDelta)) ? Math.trunc(Number(sekaDelta)) : null;
  const sekaLineLabel = String(outcome?.display?.sekaLineLabel || 'Стол').trim() || 'Стол';

  const bucksDeltaRaw = outcome?.currencyDelta?.bucks;
  const bucksDeltaNum =
    bucksDeltaRaw != null && Number.isFinite(Number(bucksDeltaRaw)) ? Math.trunc(Number(bucksDeltaRaw)) : 0;

  const walletSekaDelta = useMemo(() => {
    const explicit = outcome?.display?.walletSekaDelta;
    if (explicit != null && Number.isFinite(Number(explicit))) return Math.trunc(Number(explicit));
    if (sekaLineLabel === 'Кошелёк' && sekaDeltaNum != null) return sekaDeltaNum;
    return 0;
  }, [outcome?.display?.walletSekaDelta, sekaLineLabel, sekaDeltaNum]);

  const sekaFrom = useMemo(() => {
    const before = outcome?.balanceBefore?.seka;
    if (before != null && Number.isFinite(Number(before))) return Math.max(0, Number(before));
    if (sekaDeltaNum != null && Number.isFinite(Number(sekaDeltaNum))) return Math.max(0, sekaAfter - sekaDeltaNum);
    return Math.max(0, sekaAfter - walletSekaDelta);
  }, [outcome?.balanceBefore?.seka, sekaAfter, walletSekaDelta, sekaDeltaNum]);

  const bucksFrom = useMemo(() => {
    const before = outcome?.balanceBefore?.bucks;
    if (before != null && Number.isFinite(Number(before))) return Math.max(0, Number(before));
    return Math.max(0, bucksAfter - bucksDeltaNum);
  }, [outcome?.balanceBefore?.bucks, bucksAfter, bucksDeltaNum]);

  const respectFrom = useMemo(() => {
    if (respectDeltaRaw <= 0) return 0;
    return Math.max(0, respectDeltaRaw - Math.min(24, respectDeltaRaw));
  }, [respectDeltaRaw]);
  /** Любой из согласованных «плюсов» SEKA за сессию — стол/дельта/кошелёк/баланс. */
  const sekaWinForRespectSfx = useMemo(
    () =>
      Math.max(
        0,
        sekaAfter - sekaFrom,
        sekaDeltaNum != null && sekaDeltaNum > 0 ? sekaDeltaNum : 0,
        walletSekaDelta > 0 ? walletSekaDelta : 0
      ),
    [sekaAfter, sekaFrom, sekaDeltaNum, walletSekaDelta]
  );
  const respectCompleteSoundKey = useMemo(() => {
    if (isLose) return 'overlay-respect-defeat';
    if (sekaWinForRespectSfx >= 1000) return 'overlay-respect-complete-big';
    return 'overlay-respect-complete';
  }, [isLose, sekaWinForRespectSfx]);
  const sekaTrend = sekaAfter > sekaFrom ? 'up' : sekaAfter < sekaFrom ? 'down' : 'flat';
  const deltaToneClass =
    sekaDeltaNum == null || sekaDeltaNum === 0
      ? 'text-amber-100/92'
      : sekaDeltaNum > 0
        ? 'text-emerald-200 drop-shadow-[0_0_10px_rgba(52,211,153,0.42)] animate-pulse'
        : 'text-rose-200 drop-shadow-[0_0_10px_rgba(251,113,133,0.42)] animate-pulse';
  const hasSekaDelta = sekaDeltaNum != null && sekaDeltaNum !== 0;
  const hasBucksDelta = bucksDeltaNum !== 0;
  const hasProgress = Boolean(pb && pa);
  const hasUnlock = unlocked.length > 0;
  const primaryCurrency = useMemo(() => {
    if (hasSekaDelta) return 'seka';
    if (hasBucksDelta) return 'bucks';
    // Даже без дельты показываем валютный шаг первым (пользователь видит текущий итог по SEKA).
    return 'seka';
  }, [hasSekaDelta, hasBucksDelta]);
  const tickerSteps = useMemo(() => {
    const steps = [];
    steps.push({ kind: 'currency', currency: primaryCurrency });
    if (hasProgress) steps.push({ kind: 'progress' });
    if (hasUnlock) steps.push({ kind: 'unlock' });
    return steps;
  }, [primaryCurrency, hasProgress, hasUnlock]);

  /** Совпадает с таймингами переключения шагов ниже (anim + hold + transition между шагами) + 3 с после последнего шага. */
  const matchAbortDismissMinMs = useMemo(() => {
    if (!isMatchAbortOutcome) return 0;
    const POST_MS = 3000;
    const n = tickerSteps.length;
    if (n <= 0) return 4000 + POST_MS;
    let acc = 0;
    for (let i = 0; i < n; i++) {
      const step = tickerSteps[i];
      const anim =
        step?.kind === 'currency' ? 980 : step?.kind === 'progress' ? 1100 : 900;
      acc += anim + 2000;
      if (i < n - 1) acc += 280;
    }
    return acc + POST_MS;
  }, [isMatchAbortOutcome, tickerSteps]);

  const durationMs = Math.max(
    1200,
    Math.floor(Number(outcome?.display?.durationMs) || 3000),
    matchAbortDismissMinMs
  );

  const [tickerIndex, setTickerIndex] = useState(0);
  const [isStepTransitioning, setIsStepTransitioning] = useState(false);
  const currentStep = tickerSteps[tickerIndex] || null;

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!autoDismiss) return undefined;
    const hideTimer = setTimeout(() => onCloseRef.current?.(), durationMs);
    return () => clearTimeout(hideTimer);
  }, [durationMs, outcome?.outcomeId, autoDismiss]);

  useEffect(() => {
    setTickerIndex(0);
    setIsStepTransitioning(false);
  }, [outcomeToken]);

  useEffect(() => {
    if (tickerSteps.length <= 1) return undefined;
    if (tickerIndex >= tickerSteps.length - 1) return undefined;
    const step = tickerSteps[tickerIndex];
    const animDuration =
      step?.kind === 'currency' ? 980 : step?.kind === 'progress' ? 1100 : 900;
    const holdAfterAnimMs = 2000;
    const transitionMs = 280;
    const transitionStart = setTimeout(() => {
      setIsStepTransitioning(true);
    }, animDuration + holdAfterAnimMs);
    const t = setTimeout(() => {
      setTickerIndex((prev) => Math.min(prev + 1, tickerSteps.length - 1));
      setIsStepTransitioning(false);
    }, animDuration + holdAfterAnimMs + transitionMs);
    return () => {
      clearTimeout(transitionStart);
      clearTimeout(t);
    };
  }, [tickerIndex, tickerSteps]);

  const shell = isWin
    ? 'border-emerald-300/45 from-[#163427]/95 via-[#10281e]/95 to-[#09160f]/95'
    : isLose
      ? 'border-rose-300/45 from-[#3a1822]/95 via-[#2a1017]/95 to-[#15070c]/95'
      : 'border-cyan-300/45 from-[#153546]/95 via-[#102634]/95 to-[#091420]/95';
  const accent = isWin ? 'text-emerald-200' : isLose ? 'text-rose-200' : 'text-cyan-200';
  const title = isWin ? 'ПОБЕДА' : isLose ? 'ПОРАЖЕНИЕ' : 'ИТОГ';
  const subtitleDefault = isWin ? 'Сессия завершена успешно' : isLose ? 'Сессия завершена' : 'Результат зафиксирован';
  const subtitle = String(outcome?.display?.subtitle || '').trim() || subtitleDefault;

  const barGradient = isWin
    ? 'from-emerald-400 via-teal-300 to-cyan-200'
    : isLose
      ? 'from-rose-500 via-amber-500 to-amber-200'
      : 'from-cyan-400 via-violet-400 to-fuchsia-300';

  return (
    <div
      className={cn(
        'fixed inset-0 z-[170] flex items-center justify-center p-2 sm:p-3',
        autoDismiss ? 'pointer-events-none' : 'pointer-events-auto bg-black/45 backdrop-blur-[2px]'
      )}
    >
      <div
        className={cn(
          'relative w-full max-w-[min(92vw,320px)] overflow-hidden rounded-xl border bg-gradient-to-b p-2.5 shadow-[0_10px_36px_rgba(0,0,0,0.58)] backdrop-blur-md sm:max-w-[min(92vw,340px)] sm:rounded-2xl sm:p-3',
          shell,
          !autoDismiss && 'pointer-events-auto'
        )}
      >
        <div className="pointer-events-none absolute inset-0 opacity-30" aria-hidden>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_0%,rgba(255,255,255,0.2),transparent_42%)]" />
        </div>
        <div className="relative z-[1]">
          <style
            dangerouslySetInnerHTML={{
              __html: `
                @keyframes srRespectLineIn {
                  0% { transform: scale(0.88) translateY(8px); opacity: 0; filter: blur(6px); }
                  50% { transform: scale(1.07) translateY(0); opacity: 1; filter: blur(0); }
                  100% { transform: scale(1) translateY(0); opacity: 1; filter: blur(0); }
                }
                @keyframes srRespectHalo {
                  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.45); }
                  35% { opacity: 0.65; }
                  100% { opacity: 0; transform: translate(-50%, -50%) scale(1.4); }
                }
                @keyframes srRespectShine {
                  0% { transform: translateX(-130%); opacity: 0; }
                  25% { opacity: 0.45; }
                  100% { transform: translateX(130%); opacity: 0; }
                }
              `,
            }}
          />
          <p className={`text-center text-[10px] font-bold tracking-[0.2em] ${accent}`}>{title}</p>
          <p className="mt-0.5 text-center text-[11px] font-medium leading-tight text-white/82">{subtitle}</p>

          <div
            className={cn(
              'mt-2 rounded-lg border border-white/12 bg-black/38 px-2 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] transition-all duration-300 ease-out',
              isStepTransitioning
                ? 'opacity-0 -translate-y-1 scale-[0.985] blur-[1px]'
                : 'opacity-100 translate-y-0 scale-100 blur-0'
            )}
          >
            {currentStep?.kind === 'currency' && currentStep.currency === 'seka' ? (
              <div className="text-center">
                <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-white/55">SEKA</p>
                <p
                  className={cn(
                    'mt-0.5 text-lg font-black tabular-nums leading-none',
                    sekaTrend === 'up' && 'text-emerald-100 drop-shadow-[0_0_12px_rgba(52,211,153,0.45)]',
                    sekaTrend === 'down' && 'text-rose-100 drop-shadow-[0_0_12px_rgba(251,113,133,0.42)]',
                    sekaTrend === 'flat' && 'text-white'
                  )}
                >
                  <OverlayCountNumber
                  from={sekaFrom}
                  to={sekaAfter}
                  outcomeToken={outcomeToken}
                  durationMs={900}
                  enableBalanceGainSfx={sekaAfter > sekaFrom}
                />
                </p>
                <p className={cn('mt-0.5 text-[10px] font-bold tabular-nums', deltaToneClass)}>
                  {sekaLineLabel}:{' '}
                  <OverlayCountNumber from={0} to={sekaDeltaNum || 0} outcomeToken={outcomeToken} durationMs={860} signed /> SEKA
                </p>
              </div>
            ) : null}

            {currentStep?.kind === 'currency' && currentStep.currency === 'bucks' ? (
              <div className="text-center">
                <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-white/55">BUCKS</p>
                <p
                  className={cn(
                    'mt-0.5 text-lg font-black tabular-nums leading-none',
                    bucksAfter > bucksFrom && 'text-emerald-100 drop-shadow-[0_0_12px_rgba(52,211,153,0.45)]',
                    bucksAfter < bucksFrom && 'text-rose-100 drop-shadow-[0_0_12px_rgba(251,113,133,0.42)]',
                    bucksAfter === bucksFrom && 'text-white'
                  )}
                >
                  <OverlayCountNumber
                  from={bucksFrom}
                  to={bucksAfter}
                  outcomeToken={outcomeToken}
                  durationMs={900}
                  enableBalanceGainSfx={bucksAfter > bucksFrom}
                />
                </p>
                <p
                  className={cn(
                    'mt-0.5 text-[10px] font-bold tabular-nums',
                    bucksDeltaNum > 0 && 'text-emerald-200 animate-pulse',
                    bucksDeltaNum < 0 && 'text-rose-200 animate-pulse',
                    bucksDeltaNum === 0 && 'text-white/70'
                  )}
                >
                  Δ: <OverlayCountNumber from={0} to={bucksDeltaNum} outcomeToken={outcomeToken} durationMs={860} signed /> BUCKS
                </p>
              </div>
            ) : null}

            {currentStep?.kind === 'progress' && pb && pa ? (
              <div className="relative overflow-visible pt-0.5">
                {respectDeltaRaw !== 0 ? (
                  <>
                    <div
                      className={cn(
                        'pointer-events-none absolute left-1/2 top-[42%] z-0 h-20 w-[118%] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-0 blur-2xl [animation:srRespectHalo_1.35s_ease-out_forwards]',
                        isWin && 'bg-emerald-400/50',
                        isLose && 'bg-rose-400/45',
                        !isWin && !isLose && 'bg-cyan-400/40'
                      )}
                      aria-hidden
                    />
                    <div
                      className="pointer-events-none absolute inset-x-1 top-1 z-[1] h-5 overflow-hidden rounded-md opacity-90"
                      aria-hidden
                    >
                      <div className="absolute inset-y-0 w-[45%] bg-gradient-to-r from-transparent via-white/55 to-transparent [animation:srRespectShine_1.05s_ease-in-out_0.12s_forwards]" />
                    </div>
                  </>
                ) : null}
                <p
                  className={cn(
                    'relative z-[2] text-center text-base font-black tabular-nums leading-none [animation:srRespectLineIn_0.9s_cubic-bezier(0.22,1,0.36,1)_both]',
                    accent,
                    respectDeltaRaw !== 0 &&
                      'motion-reduce:[animation:none] motion-reduce:opacity-100 motion-reduce:transform-none',
                    respectDeltaRaw !== 0 &&
                      (isWin
                        ? 'drop-shadow-[0_0_18px_rgba(52,211,153,0.38)]'
                        : isLose
                          ? 'drop-shadow-[0_0_18px_rgba(251,113,133,0.34)]'
                          : 'drop-shadow-[0_0_16px_rgba(34,211,238,0.3)]')
                  )}
                >
                  <OverlayCountNumber
                    from={respectDeltaRaw < 0 ? 0 : respectFrom}
                    to={respectDeltaRaw}
                    outcomeToken={outcomeToken}
                    durationMs={920}
                    signed
                    enableRespectGainSfx={respectDeltaRaw > 0 && respectFrom < respectDeltaRaw}
                    respectCompleteSoundKey={respectCompleteSoundKey}
                  />{' '}
                  респекта
                </p>
                <div className="relative z-[2]">
                  <SessionRespectProgress pb={pb} pa={pa} outcomeToken={outcomeToken} barGradientClass={barGradient} />
                </div>
              </div>
            ) : null}

            {currentStep?.kind === 'unlock' ? (
              <div className="text-center">
                <p className="text-[9px] font-bold uppercase tracking-wide text-amber-200/95">Новая ступень</p>
                <p className="mt-0.5 text-[11px] font-semibold leading-snug text-amber-50/95">
                  {String(unlocked[unlocked.length - 1]?.title || 'Награда')}
                </p>
              </div>
            ) : null}

            {!currentStep ? (
              <div className="text-center text-[10px] text-white/65">
                {tableChips != null ? `На столе: ${tableChips.toLocaleString('ru-RU')}` : 'Результат зафиксирован'}
              </div>
            ) : null}
          </div>

          {!autoDismiss && typeof onClose === 'function' ? (
            <button
              type="button"
              onClick={() => onClose()}
              className="lobby-focus mt-2 w-full rounded-lg border border-white/22 bg-white/10 py-1.5 text-[11px] font-bold text-white transition hover:bg-white/14"
            >
              Закрыть
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// Компонент для анимированного счетчика
export const AnimatedCounter = ({ value, duration = 1000, prefix = '', suffix = '' }) => {
  const [displayValue, setDisplayValue] = React.useState(0);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    if (value !== displayValue) {
      setIsAnimating(true);
      const startValue = displayValue;
      const endValue = value;
      const startTime = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for smooth animation
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        const currentValue = startValue + (endValue - startValue) * easeOutCubic;
        
        setDisplayValue(Math.round(currentValue * 100) / 100);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setIsAnimating(false);
        }
      };
      
      requestAnimationFrame(animate);
    }
  }, [value, duration, displayValue]);

  return (
    <span 
      ref={counterRef}
      style={{
        display: 'inline-block',
        transform: isAnimating ? 'scale(1.05)' : 'scale(1)',
        transition: 'transform 0.2s ease',
        color: isAnimating ? '#ffd700' : 'inherit',
        textShadow: isAnimating ? '0 0 10px rgba(255, 215, 0, 0.5)' : 'none'
      }}
    >
      {prefix}{displayValue.toLocaleString()}{suffix}
    </span>
  );
};

// Компонент для анимированных монет/чипов
export const FlyingCoins = ({ amount, fromElement, toElement, onComplete }) => {
  const [coins, setCoins] = React.useState([]);
  const containerRef = useRef(null);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (amount > 0 && fromElement && toElement) {
      const coinCount = Math.min(Math.floor(amount / 10) + 1, 10);
      const newCoins = [];

      for (let i = 0; i < coinCount; i++) {
        const coin = {
          id: Date.now() + i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          delay: i * 100,
          duration: 1000 + Math.random() * 500
        };
        newCoins.push(coin);
      }

      setCoins(newCoins);

      // Clean up coins after animation
      const timer = setTimeout(() => {
        setCoins([]);
        if (onCompleteRef.current) onCompleteRef.current();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [amount, fromElement, toElement]);

  return (
    <div 
      ref={containerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9999
      }}
    >
      {coins.map(coin => (
        <div
          key={coin.id}
          style={{
            position: 'absolute',
            left: `${coin.x}%`,
            top: `${coin.y}%`,
            fontSize: '20px',
            color: '#ffd700',
            animation: `flyCoin ${coin.duration}ms ease-out ${coin.delay}ms forwards`,
            textShadow: '0 0 10px rgba(255, 215, 0, 0.8)'
          }}
        >
          💰
        </div>
      ))}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes flyCoin {
          0% {
            transform: translate(0, 0) scale(1) rotate(0deg);
            opacity: 1;
          }
          50% {
            transform: translate(50px, -50px) scale(1.2) rotate(180deg);
            opacity: 0.8;
          }
          100% {
            transform: translate(100px, -100px) scale(0.5) rotate(360deg);
            opacity: 0;
          }
        }
      `,
        }}
      />
    </div>
  );
};

// Компонент для прогресс-бара с анимацией
export const AnimatedProgressBar = ({ 
  value, 
  max = 100, 
  color = '#ffd700', 
  height = '8px',
  showPercentage = true,
  label = ''
}) => {
  const [animatedValue, setAnimatedValue] = React.useState(0);
  const animatedValueRef = useRef(animatedValue);
  animatedValueRef.current = animatedValue;
  const progressRef = useRef(null);

  useEffect(() => {
    const startTime = Date.now();
    const duration = 1000;
    const startValue = animatedValueRef.current;
    const endValue = value;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (endValue - startValue) * easeOutCubic;
      
      setAnimatedValue(currentValue);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [value]);

  const percentage = Math.round((animatedValue / max) * 100);

  return (
    <div style={{ width: '100%', marginBottom: '10px' }}>
      {label && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginBottom: '5px',
          fontSize: '0.9rem',
          color: 'rgba(255, 255, 255, 0.8)'
        }}>
          <span>{label}</span>
          {showPercentage && <span>{percentage}%</span>}
        </div>
      )}
      <div
        ref={progressRef}
        style={{
          width: '100%',
          height: height,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '4px',
          overflow: 'hidden',
          backdropFilter: 'blur(5px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}
      >
        <div
          style={{
            width: `${percentage}%`,
            height: '100%',
            background: `linear-gradient(90deg, ${color} 0%, ${color}dd 100%)`,
            borderRadius: '4px',
            transition: 'width 0.3s ease',
            boxShadow: `0 0 10px ${color}40`,
            position: 'relative'
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)`,
              animation: 'shimmer 2s ease-in-out infinite'
            }}
          />
        </div>
      </div>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `,
        }}
      />
    </div>
  );
};

// Компонент для пульсирующих элементов
export const PulsingElement = ({ children, intensity = 'medium', color = '#ffd700' }) => {
  const getPulseStyle = () => {
    const baseStyle = {
      display: 'inline-block',
      animation: `pulse-${intensity} 2s ease-in-out infinite`
    };

    switch (intensity) {
      case 'low':
        return { ...baseStyle, animation: 'pulse-low 3s ease-in-out infinite' };
      case 'high':
        return { ...baseStyle, animation: 'pulse-high 1s ease-in-out infinite' };
      default:
        return baseStyle;
    }
  };

  return (
    <div style={getPulseStyle()}>
      {children}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes pulse-low {
          0%, 100% { 
            box-shadow: 0 0 0 0 ${color}40;
            transform: scale(1);
          }
          50% { 
            box-shadow: 0 0 0 5px ${color}20;
            transform: scale(1.02);
          }
        }
        @keyframes pulse-medium {
          0%, 100% { 
            box-shadow: 0 0 0 0 ${color}60;
            transform: scale(1);
          }
          50% { 
            box-shadow: 0 0 0 10px ${color}30;
            transform: scale(1.05);
          }
        }
        @keyframes pulse-high {
          0%, 100% { 
            box-shadow: 0 0 0 0 ${color}80;
            transform: scale(1);
          }
          50% { 
            box-shadow: 0 0 0 15px ${color}40;
            transform: scale(1.1);
          }
        }
      `,
        }}
      />
    </div>
  );
};

// Компонент для мерцающих элементов
export const FlickeringElement = ({ children, speed = 'normal' }) => {
  const getFlickerStyle = () => {
    switch (speed) {
      case 'slow':
        return { animation: 'flicker-slow 3s ease-in-out infinite' };
      case 'fast':
        return { animation: 'flicker-fast 0.5s ease-in-out infinite' };
      default:
        return { animation: 'flicker-normal 1s ease-in-out infinite' };
    }
  };

  return (
    <div style={getFlickerStyle()}>
      {children}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        @keyframes flicker-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        @keyframes flicker-normal {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        @keyframes flicker-fast {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `,
        }}
      />
    </div>
  );
};

const gameEffectsDefaultExport = {
  NotificationToast,
  AnimatedCounter,
  FlyingCoins,
  AnimatedProgressBar,
  PulsingElement,
  FlickeringElement,
};

export default gameEffectsDefaultExport;
